import { ClockIcon, UserIcon } from "@heroicons/react/24/outline";
import {
    formatDateBlock,
    formatTimeRange,
    ReferenceContactId,
    User,
    UserId,
} from "@sp-crm/core";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
    ContentFull,
    ContentHeader,
    ContentSection2Up,
    ContentSectionColumn,
} from "components/layout";
import { trpc } from "components/rpc/trpc";
import React, { useCallback, useMemo, useState } from "react";
import { useUsers } from "store/selectors/hooks";
import { breakNewlines } from "util/text";
import { handleEvent } from "util/user-events";
import { DeleteButton, EditButton } from "../ui/action-button";
import { ClampedText } from "../ui/clamped-text";
import {
    CommunityLink,
    ReferenceBusinessLink,
    ReferenceContactLink,
} from "../ui/entity-link";
import { MapLink } from "../ui/map-link";
import { PrimaryButton } from "../ui/primary-button";
import { Spinner } from "../ui/spinner";
import { CalendarEventDto, VisitParent } from "./types";
import { VisitEditor } from "./visit-editor";

interface VisitsSectionProps {
    parent: VisitParent;
}

const formatLocalDateIso = (date: Date): string => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
};

const splitVisitsByDate = (
    visits: CalendarEventDto[],
): { upcoming: CalendarEventDto[]; past: CalendarEventDto[] } => {
    const todayKey = formatLocalDateIso(new Date());
    const upcoming: CalendarEventDto[] = [];
    const past: CalendarEventDto[] = [];
    for (const v of visits) {
        if (v.eventDate >= todayKey) {
            upcoming.push(v);
        } else {
            past.push(v);
        }
    }
    upcoming.sort((a, b) => a.eventDate.localeCompare(b.eventDate));
    return { upcoming, past };
};

const VisitItem: React.FC<{
    visit: CalendarEventDto;
    users: Record<UserId, User>;
    parentKind: VisitParent["kind"];
    onEdit: (visit: CalendarEventDto) => void;
    onDelete: (visit: CalendarEventDto) => void;
}> = ({ visit, users, parentKind, onEdit, onDelete }) => {
    const userName = users[visit.userId]?.name || "";
    const snapshotAddress = visit.businessAddress ?? visit.communityAddress;
    const showPlace = parentKind === "contact";

    const handleEdit = useCallback(() => {
        onEdit(visit);
    }, [onEdit, visit]);

    const handleDelete = useCallback(() => {
        onDelete(visit);
    }, [onDelete, visit]);

    const dateBlock = formatDateBlock(visit.eventDate);
    const timeRange = visit.eventTime
        ? formatTimeRange(visit.eventTime, visit.eventDuration)
        : null;
    const titleText = visit.title?.trim();

    return (
        <li className="bg-white rounded-xl border border-gray-200 hover:shadow-sm transition p-4">
            <div className="flex items-start gap-4">
                {dateBlock ? (
                    <div className="flex flex-col items-center justify-center w-14 flex-shrink-0">
                        <div className="text-[11px] font-semibold uppercase text-brand-600">
                            {dateBlock.month}
                        </div>
                        <div className="text-2xl font-bold text-gray-900 leading-none">
                            {dateBlock.day}
                        </div>
                        <div className="text-[11px] text-gray-400">
                            {dateBlock.weekday}
                        </div>
                    </div>
                ) : null}
                <div className="flex-1 min-w-0">
                    {titleText ? (
                        <div className="font-medium text-gray-900">{titleText}</div>
                    ) : null}
                    {timeRange ? (
                        <div className="flex items-center gap-1 text-sm text-gray-500 mt-1">
                            <ClockIcon className="w-4 h-4" aria-hidden="true" />
                            <span>{timeRange}</span>
                        </div>
                    ) : null}
                    {userName ? (
                        <div className="flex items-center gap-1 text-sm text-gray-500 mt-1">
                            <UserIcon className="w-4 h-4" aria-hidden="true" />
                            <span>{userName}</span>
                        </div>
                    ) : null}
                    {showPlace && visit.businessId && visit.businessName ? (
                        <div className="mt-1">
                            <ReferenceBusinessLink
                                referenceBusinessId={visit.businessId}
                                name={visit.businessName}
                            />
                        </div>
                    ) : showPlace && visit.communityId && visit.communityName ? (
                        <div className="mt-1">
                            <CommunityLink
                                communityId={visit.communityId}
                                name={visit.communityName}
                            />
                        </div>
                    ) : null}
                    {snapshotAddress ? (
                        <div className="mt-1">
                            <MapLink address={snapshotAddress} />
                        </div>
                    ) : null}
                    {parentKind === "organization" && visit.parentContactName ? (
                        <div className="mt-1">
                            <ReferenceContactLink
                                referenceContactId={visit.parentId as ReferenceContactId}
                                name={visit.parentContactName}
                            />
                        </div>
                    ) : null}
                    {visit.privateNotes ? (
                        <p className="text-sm text-gray-600 mt-2">
                            <ClampedText lines={3}>
                                {breakNewlines(visit.privateNotes)}
                            </ClampedText>
                        </p>
                    ) : null}
                    {visit.outcome ? (
                        <div className="mt-2 text-sm bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 text-emerald-900">
                            <span className="font-semibold">Outcome: </span>
                            <ClampedText lines={3}>
                                {breakNewlines(visit.outcome)}
                            </ClampedText>
                        </div>
                    ) : null}
                </div>
                <div className="flex items-center">
                    <EditButton backgroundColor="bg-white" onClick={handleEdit} />
                    <DeleteButton
                        backgroundColor="bg-white"
                        onClick={handleDelete}
                        confirm={{
                            title: "Delete Visit",
                            message: "Are you sure you want to delete this visit?",
                        }}
                    />
                </div>
            </div>
        </li>
    );
};

export const VisitsSection: React.FC<VisitsSectionProps> = ({ parent }) => {
    const trpcClient = trpc.useTRPC();
    const users = useUsers().users;
    const queryOptions = useMemo(
        () =>
            parent.kind === "organization"
                ? trpcClient.calendarEvents.listByBusiness.queryOptions({
                      businessId: parent.id,
                  })
                : trpcClient.calendarEvents.listByParent.queryOptions({
                      parentId: parent.id,
                  }),
        [trpcClient, parent.kind, parent.id],
    );
    const query = useQuery(queryOptions);
    const visits = useMemo(() => query.data || [], [query.data]);
    const { upcoming, past } = useMemo(() => splitVisitsByDate(visits), [visits]);

    const [editing, setEditing] = useState<CalendarEventDto | "new" | null>(null);

    const handleAdd = useCallback(() => {
        handleEvent("visit-add-click", { parentKind: parent.kind });
        setEditing("new");
    }, [parent.kind]);
    const handleEdit = useCallback((visit: CalendarEventDto) => setEditing(visit), []);
    const handleClose = useCallback(() => setEditing(null), []);
    const handleSaved = useCallback(() => query.refetch(), [query]);

    const deleteMutation = useMutation({
        ...trpcClient.calendarEvents.delete.mutationOptions(),
        onSuccess: () => {
            query.refetch();
        },
    });

    const handleDeleteVisit = useCallback(
        (visit: CalendarEventDto) => {
            deleteMutation.mutate({ id: visit.id });
        },
        [deleteMutation],
    );

    if (query.isLoading) {
        return (
            <div className="flex justify-center py-8">
                <Spinner />
            </div>
        );
    }

    if (query.isError) {
        return <p className="text-sm text-red-600">Failed to load visits</p>;
    }

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-end">
                <PrimaryButton onClick={handleAdd}>Add Visit</PrimaryButton>
            </div>

            <ContentSection2Up>
                <ContentSectionColumn>
                    <ContentFull>
                        <ContentHeader>Upcoming Visits</ContentHeader>
                        {upcoming.length > 0 ? (
                            <ul className="space-y-2 mt-3">
                                {upcoming.map(visit => (
                                    <VisitItem
                                        key={visit.id}
                                        visit={visit}
                                        users={users}
                                        parentKind={parent.kind}
                                        onEdit={handleEdit}
                                        onDelete={handleDeleteVisit}
                                    />
                                ))}
                            </ul>
                        ) : (
                            <p className="text-sm text-gray-500 mt-2">
                                No upcoming visits
                            </p>
                        )}
                    </ContentFull>
                </ContentSectionColumn>
                <ContentSectionColumn>
                    <ContentFull>
                        <ContentHeader>Past Visits</ContentHeader>
                        {past.length > 0 ? (
                            <ul className="space-y-2 mt-3">
                                {past.map(visit => (
                                    <VisitItem
                                        key={visit.id}
                                        visit={visit}
                                        users={users}
                                        parentKind={parent.kind}
                                        onEdit={handleEdit}
                                        onDelete={handleDeleteVisit}
                                    />
                                ))}
                            </ul>
                        ) : (
                            <p className="text-sm text-gray-500 mt-2">No past visits</p>
                        )}
                    </ContentFull>
                </ContentSectionColumn>
            </ContentSection2Up>

            {editing === "new" ? (
                <VisitEditor
                    mode="new"
                    parent={parent}
                    onSaved={handleSaved}
                    onClose={handleClose}
                />
            ) : editing !== null ? (
                <VisitEditor
                    mode="edit"
                    visit={editing}
                    onSaved={handleSaved}
                    onClose={handleClose}
                />
            ) : null}
        </div>
    );
};
